import React, { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import {
  ROUTE_DESCRIPTIONS,
  ROUTE_SEARCH_KEYWORDS,
  SIDEBAR_MENU_ITEMS,
} from '../data/routeMetadata';
import './Sidebar.css';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredMenuItems = useMemo(() => {
    if (!normalizedSearch) {
      return SIDEBAR_MENU_ITEMS;
    }

    const itemMatchesSearch = (item) => {
      const label = item.label?.toLowerCase() || '';
      const path = item.path || '';
      const description = (ROUTE_DESCRIPTIONS[path] || '').toLowerCase();
      const keywords = (ROUTE_SEARCH_KEYWORDS[path] || []).join(' ').toLowerCase();
      const haystack = `${label} ${description} ${keywords}`;

      return haystack.includes(normalizedSearch);
    };

    return SIDEBAR_MENU_ITEMS
      .map((item) => {
        if (item.path) {
          return itemMatchesSearch(item) ? item : null;
        }

        const categoryMatches = item.category.toLowerCase().includes(normalizedSearch);
        const matchingItems = item.items.filter((subItem) => itemMatchesSearch(subItem));

        if (categoryMatches) {
          return item;
        }

        if (matchingItems.length > 0) {
          return { ...item, items: matchingItems };
        }

        return null;
      })
      .filter(Boolean);
  }, [normalizedSearch]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button className="sidebar-toggle" onClick={toggleSidebar}>
        {isOpen ? <FaTimes /> : <FaBars />}
      </button>
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <nav className="sidebar-nav">
          <div className="sidebar-search-wrapper">
            <input
              type="search"
              className="sidebar-search"
              placeholder="Search calculators and blogs..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search calculators and blogs"
            />
          </div>
          {filteredMenuItems.map((item, index) => {
            if (item.path) {
              return (
                <NavLink
                  key={index}
                  to={item.path}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setIsOpen(false)}
                >
                  <item.icon className="nav-icon" />
                  <span>{item.label}</span>
                </NavLink>
              );
            }
            return (
              <div key={index} className="nav-category">
                <h4 className="category-title">{item.category}</h4>
                {item.items.map((subItem, subIndex) => (
                  <NavLink
                    key={subIndex}
                    to={subItem.path}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => setIsOpen(false)}
                  >
                    <subItem.icon className="nav-icon" />
                    <span>{subItem.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
          {filteredMenuItems.length === 0 && (
            <p className="sidebar-empty-state">No calculators found.</p>
          )}
        </nav>
      </aside>
      {isOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}
    </>
  );
};

export default Sidebar;
